import { useEffect, useState } from "react";
import { coverStyle } from "@/lib/cover";
import { Link } from "react-router-dom";
import { EventLayout } from "@/components/EventLayout";
import { useEvent } from "@/context/EventContext";
import { Users, CheckCircle2, Clock, XCircle, Calendar, MapPin, Copy, ExternalLink, Sparkles, Images, ArrowRight, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { event, role, features, eapi, eventId, error } = useEvent();
  const [guests, setGuests] = useState([]);

  useEffect(() => {
    if (role === "host") eapi.get("/guests").then(({ data }) => setGuests(data)).catch(() => setGuests([]));
  }, [role, eapi]);

  if (error) return (
    <EventLayout>
      <div className="p-16 text-center max-w-md mx-auto" data-testid="event-access-error">
        <h2 className="font-serif text-3xl">You don't have access to this event</h2>
        <p className="text-stone-500 mt-3 text-sm">This event may be private, or you may need to sign in with the email address your host invited you with.</p>
        <Link to="/app" data-testid="access-error-back" className="inline-block mt-6 rounded-full bg-[#2C2A28] text-white px-6 py-3 text-sm hover:bg-[#8C9A86] transition-colors">Back to my events</Link>
      </div>
    </EventLayout>
  );
  if (!event) return <EventLayout><div className="p-16 text-center font-serif text-2xl text-stone-300">Loading…</div></EventLayout>;

  const unlocked = features?.unlocked || [];
  const published = ["invites", "thankyou", "gallery"].every((k) => unlocked.includes(k));
  const publicUrl = `${window.location.origin}/w/${event.public_slug}`;
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Public link copied"); };

  const active = guests.filter((g) => g.approval_status !== "rejected");
  const attending = active.filter((g) => g.rsvp_status === "attending").length;
  const declined = active.filter((g) => g.rsvp_status === "declined").length;
  const pending = active.filter((g) => g.rsvp_status === "pending").length;

  return (
    <EventLayout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
        {/* Hero */}
        <div className="relative rounded-2xl overflow-hidden h-56 sm:h-64 mb-8">
          <img src={event.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" style={coverStyle(event)} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10" />
          <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 text-white">
            <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/90 text-stone-700 w-fit mb-2">{event.template?.name}</span>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight" style={{ fontFamily: event.theme_font || undefined }}>{event.title}</h1>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-white/85">
              {event.date && <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDate(event.date)}</span>}
              {event.venue_name && <span className="flex items-center gap-1.5"><MapPin size={14} /> {event.venue_name}</span>}
            </div>
          </div>
        </div>

        {role === "attendee" ? (
          <div className="bg-white border border-[#E8E6E1] rounded-xl p-8 text-center">
            <Images size={26} className="mx-auto text-[#8C9A86] mb-3" />
            <h2 className="font-serif text-2xl">You attended this event</h2>
            <p className="text-stone-500 mt-2 text-sm">Relive the memories whenever you like.</p>
            <Link to={`/e/${eventId}/gallery`} data-testid="view-gallery-cta" className="inline-flex items-center gap-2 mt-5 rounded-full bg-[#2C2A28] text-white px-6 py-3 text-sm hover:bg-[#8C9A86] transition-colors">View the gallery <ArrowRight size={15} /></Link>
          </div>
        ) : (
          <>
            {!published && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-[#2C2A28] text-white rounded-xl px-6 py-5 mb-8" data-testid="publish-banner">
                <Sparkles size={20} className="text-[#D4C5B3] shrink-0" />
                <div className="flex-1">
                  <p className="font-serif text-xl">Your event page isn't live yet</p>
                  <p className="text-sm text-white/70 mt-0.5">Unlock the Guest List & Invitations bundle (Invitations, Thank-You and Gallery) to make your public guest page live. You can still preview it below.</p>
                </div>
                <Link to={`/e/${eventId}/pricing`} data-testid="dashboard-unlock-cta" className="rounded-full bg-white text-stone-900 px-6 py-2.5 text-sm font-medium hover:bg-[#D4C5B3] transition-colors whitespace-nowrap text-center">Unlock features</Link>
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat icon={Users} label="Total guests" value={active.length} />
              <Stat icon={CheckCircle2} label="Attending" value={attending} accent="#8C9A86" />
              <Stat icon={Clock} label="Pending" value={pending} accent="#C6A15B" />
              <Stat icon={XCircle} label="Declined" value={declined} accent="#C67A7A" />
            </div>

            <div className="bg-white border border-[#E8E6E1] rounded-xl p-6 flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
              <div className="flex-1">
                <p className="overline">Your public event page</p>
                <p className="text-sm text-stone-500 mt-1 truncate">{published ? publicUrl : "See exactly what your guests will see before you publish."}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="preview-event-page-btn" className="flex items-center gap-1.5 rounded-full border border-[#8C9A86] text-[#5f6b59] px-4 py-2 text-sm hover:bg-[#8C9A86]/10 transition-colors"><Eye size={14} /> Preview my event page</a>
                {published && <button onClick={copyLink} data-testid="copy-link-btn" className="flex items-center gap-1.5 rounded-full border border-[#E8E6E1] px-4 py-2 text-sm hover:bg-stone-50"><Copy size={14} /> Copy link</button>}
              </div>
            </div>
          </>
        )}
      </div>
    </EventLayout>
  );
}

function Stat({ icon: Icon, label, value, accent = "#2C2A28" }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-xl p-5">
      <Icon size={18} style={{ color: accent }} />
      <p className="font-serif text-4xl mt-3" style={{ color: accent }}>{value}</p>
      <p className="text-xs text-stone-400 mt-1">{label}</p>
    </div>
  );
}
function formatDate(d) {
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return d; }
}
